import { Component, Prop, Ref } from 'vue-property-decorator'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import {
    AssayEvaluationAssayResultCreateRequest,
    AssayEvaluationAssayResultUpdateRequest,
    AssaysEvaluationAssayGridResponse,
    AssaysEvaluationsGridState,
    initialAssaysEvaluationsGridState
} from '@/modules/diagnosisModule/components/assays-evaluations-grid/assays-evaluations-grid.models'
import BaseCrudClientSideComponent from '@/shared/resources/base-crud-client-side.component'
import { assaysEvaluationServices } from '@/modules/diagnosisModule/services/assays-evaluation-services'
import AssaysEvaluationsGridSidePanel
    from '@/modules/diagnosisModule/components/assays-evaluations-grid-sidepanel/assays-evaluations-grid-sidepanel.vue'
import AssaysEvaluationsModalComponent
    from '@/modules/diagnosisModule/components/assays-evaluations-modal/assays-evaluations-modal.vue'
import AssaysEvaluationsModal from '@/modules/diagnosisModule/components/assays-evaluations-modal/assays-evaluations-modal'
import { StoreUtils } from '@/shared/util/store-utils'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        VtGrid,
        AssaysEvaluationsGridSidePanel,
        AssaysEvaluationsModalComponent,
        GridDisplayOptions
    }
})
export default class AssaysEvaluationsGrid extends BaseCrudClientSideComponent<AssaysEvaluationAssayGridResponse> implements BaseCrud {
    @Prop() readonly laboratoryId!: number;
    @Prop() readonly caseId!: number;
    @Prop() readonly sampleId!: number
    @Ref() readonly assaysEvaluationsModalWrapper!: any
    @Ref() readonly assaysEvaluationsModal!: AssaysEvaluationsModal

    state: AssaysEvaluationsGridState
    constructor() {
        super()
        this.state = initialAssaysEvaluationsGridState(this.accreditationAssayActive)
    }

    async mounted(): Promise<void> {
        await this.reloadData()
    }

    get accreditationAssayActive(): boolean {
        const selectedLaboratoryConfigurationFrontParameters =
            StoreUtils.selectedLaboratoryConfiguration?.frontLaboratoryParameters
        const accreditationAssayActive =
            selectedLaboratoryConfigurationFrontParameters?.find(
                frontLaboratoryParameter => frontLaboratoryParameter.parameter.code === 'configuration.parameter.laboratory.accreditation.assays.active')
        return accreditationAssayActive?.value === 'true'
    }

    async reloadData(): Promise<void> {
        const params = {
            laboratoryId: this.laboratoryId,
            caseId: this.caseId,
            sampleId: this.sampleId
        }
        await this.getAssayResults(params)
    }

    showInGlobalLocator(label:any) {
        this.$eventHub.$emit('show-in-global-locator', label)
    }

    onCloseAdditionalSidePanel(): void {
        this.closeSidePanel()
    }

    async onSubportionCommentUpdate(subportionMap: AssayEvaluationAssayResultUpdateRequest): Promise<void> {
        await this.patchOperation(subportionMap)
    }

    async onSubportionCommentCreate(subportionMap: AssayEvaluationAssayResultCreateRequest): Promise<void> {
        await this.createOperation(subportionMap)
    }

    async getAssayResults(params: any): Promise<void> {
        const { laboratoryId, caseId, sampleId } = params
        const [error, response] = await this.$_toCall(assaysEvaluationServices.getAssaysEvaluation(laboratoryId, caseId, sampleId), true)
        if (error) {
            return
        }
        this.setRowData(response)
    }
    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        if (!params?.node) {
            return []
        } else {
            return [
                {
                    name: this.$tc('configuration.actions.update'),
                    cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                    action: () => {
                        const nodeSelected: StrictNullable<RowNode> = params?.node
                        const selectedRow: AssaysEvaluationAssayGridResponse = nodeSelected?.data
                        return this.showModal(this.$tc('configuration.actions.update'), selectedRow, this.assaysEvaluationsModalWrapper)
                    }
                },
                {
                    name: this.$tc('global.showInGlobalLocator'),
                    action: () => this.showInGlobalLocator(params?.node?.data.label),
                    cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : ['']
                }
            ]
        }
    }

    async onModalUpdate(requestMap: AssayEvaluationAssayResultUpdateRequest): Promise<void> {
        await this.patchOperation(requestMap)
    }

    async onModalCreate(requestMap: AssayEvaluationAssayResultCreateRequest): Promise<void> {
        await this.createOperation(requestMap)
    }

    async createOperation(requestMap: AssayEvaluationAssayResultCreateRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(assaysEvaluationServices.createAnAssayResult(
            this.caseId,
            this.laboratoryId,
            requestMap.portionId,
            this.sampleId,
            requestMap.subportionId,
            requestMap.requests
        ), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.assaysEvaluationsModal?.hide()
        await this.reloadData()
    }
    async patchOperation(requestMap: AssayEvaluationAssayResultUpdateRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(assaysEvaluationServices.patchAnAssayResult(
            requestMap.id,
            this.caseId,
            this.laboratoryId,
            requestMap.portionId,
            this.sampleId,
            requestMap.subportionId,
            requestMap.updateRequests
        ))
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.assaysEvaluationsModal?.hide()
        await this.reloadData()
    }

    onUpdateButtonClick(): void {
        const first = this.state.gridApi?.getSelectedRows()[0]
        this.showModal(this.$tc('configuration.actions.update'), first, this.assaysEvaluationsModalWrapper)
    }
}
